import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '@/components/SEO/SEOHead'
import { buildGraph, breadcrumbListData, faqData } from '@/data/structured-data'
import PageHeader from '@/components/Common/PageHeader'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'

const SanliurfaVeterinerPage: React.FC = () => {
  const telHref = toTelHref(contactInfo.phone)
  const waHref = toWhatsAppHref(contactInfo.whatsapp)

  const keywords = [
    'şanlıurfa veteriner', 'sanliurfa veteriner', 'urfa veteriner', 'şanlıurfa veteriner klinikleri',
    'şanlıurfa veteriner numarası', 'veteriner telefon numarası', 'veteriner numaralari', 'urfa veteriner telefon',
    '7 24 veteriner', '24 saat açık veteriner', 'gece açık veteriner', 'nöbetçi veteriner şanlıurfa',
    'kedi kısırlaştırma', 'kedi kısırlaştırma ücreti 2024', 'kedi kısırlaştırma fiyat', 'kedi kısırlaştırma ne kadar',
    'köpek kısırlaştırma', 'dişi köpeklerde kısırlaştırma', 'erkek köpek kısırlaştırma fiyat', 'köpek kısırlaştırma maliyeti',
    'köpek kastrasyon', 'kedi köpek kısırlaştırma',
    'veteriner röntgen fiyatları', 'hayvan röntgen',
    'köpek aşı fiyatları', 'kedi aşı fiyatları', 'köpek aşı takvimi', 'kedi ilk aşıları',
    'kedi iç parazit', 'kedi iç dış parazit fiyat', 'kedi iç dış parazit aşısı broadline',
    'hayvan kliniği', 'veteriner klinigi', 'en iyi veteriner kliniği', 'veteriner', 'acil veteriner', 'hayvan sagligi',
    'veterinerlik çalışma saatleri', 'veteriner yorumları', 'veteriner danışma'
  ].join(', ')

  const faqEntries = [
      {
      question: 'Şanlıurfa’da kedi kısırlaştırma ücreti 2024 ne kadar?',
      answer: 'Ücret; cins, yaş, sağlık durumu ve anestezi riskine göre değişir. Güncel bilgi için lütfen bizi arayın. Ön muayene sonrası net plan ve tahmini fiyat paylaşılır.'
    },
      {
      question: '7/24 açık acil veteriner hizmetiniz var mı?',
      answer: 'Acil durumlarda hekimimize 7/24 ulaşabilirsiniz. Uygun triyaj ve yönlendirme ile en hızlı müdahaleyi planlıyoruz.'
    },
      {
      question: 'Veteriner telefon numarası nedir?',
      answer: `Telefon: ${contactInfo.phone}. WhatsApp: ${contactInfo.whatsapp}. Randevu ve hızlı bilgi için arayabilir ya da yazabilirsiniz.`
    },
      {
      question: 'Köpek kısırlaştırma fiyatları neye göre değişir?',
      answer: 'Cins, kilo, yaş, eşlik eden hastalıklar ve anestezi riski fiyatı etkiler. Preoperatif tetkikler güvenli süreç için önerilir.'
    },
      {
      question: 'Veteriner röntgen fiyatları nedir?',
      answer: 'Röntgen bölgelerine, poz sayısına ve gerekirse sedasyona göre değişir. Dijital DR ile hızlı sonuç sunuyoruz.'
    },
      {
      question: 'Nöbetçi veteriner var mı?',
      answer: 'Mesai dışı acil durumlar için iletişime geçin; uygun kliniğe yönlendirme ve hızlı randevu desteği sağlıyoruz.'
    }
  ]

  return (
    <>
      <SEOHead
        title="Şanlıurfa Veteriner Kliniği"
        description="Şanlıurfa ve Karaköprü’de kedi-köpek kısırlaştırma, acil (7/24), röntgen ve kapsamlı veteriner hizmetleri. Randevu ve fiyat bilgisi için hemen arayın."
        keywords={keywords}
        canonicalUrl="/sanliurfa-veteriner"
        structuredData={buildGraph(
          faqData(faqEntries),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Şanlıurfa Veteriner', url: '/sanliurfa-veteriner' }
          ])
        )}
      />
      <PageHeader title="Şanlıurfa Veteriner" subtitle="Acil, kısırlaştırma, röntgen ve kapsamlı klinik hizmetleri" />

      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-bold mb-2">Kısırlaştırma (Kedi & Köpek)</h2>
              <p className="text-gray-700 leading-relaxed">
                Kedi ve köpeklerde kısırlaştırma, davranış ve sağlık açısından uzun vadeli fayda sağlar. Ücretler; cins, kilo, yaş ve
                eşlik eden sağlık durumlarına göre değişir. Güvenli anestezi için preoperatif değerlendirme ve uygun ağrı yönetimi uygularız.
              </p>
              <div className="mt-3 flex gap-3">
                <Link to="/hizmetler/cerrahi" className="btn-secondary">Cerrahi Hizmetler</Link>
                <Link to="/contact" className="btn-primary">Fiyat Bilgisi Al</Link>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-2">Acil ve 7/24 Ulaşılabilirlik</h2>
              <p className="text-gray-700 leading-relaxed">
                Triyaj, şok yönetimi ve acil stabilizasyon süreçlerinde hızlı hareket ediyoruz. Mesai dışı acil durumlarda telefon
                ve WhatsApp hattımızdan ulaşabilir, en yakın zamanda müdahale için yönlendirme alabilirsiniz.
              </p>
              <div className="mt-3 flex gap-3">
                <a href={telHref} className="btn-primary">Hemen Ara</a>
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-secondary">WhatsApp</a>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-2">Röntgen ve Görüntüleme</h2>
              <p className="text-gray-700 leading-relaxed">
                Dijital röntgen (DR) ve ultrason ile ortopedik, toraks ve abdominal değerlendirmeler yapıyoruz. Fiyatlar çekim
                bölgesine ve poz sayısına göre değişir; hızlı raporlama ile tedaviye vakit kaybetmeden başlarız.
              </p>
              <div className="mt-3">
                <Link to="/hizmetler/goruntuleme-hizmetleri" className="text-primary-600 font-semibold">Görüntüleme Hizmetleri</Link>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-2">Aşı ve Parazit Programları</h2>
              <p className="text-gray-700 leading-relaxed">
                Köpek aşı takvimi ve kedi ilk aşıları dahil olmak üzere, iç-dış parazit programlarını yaşam tarzına göre kişiselleştiriyoruz.
                Broadline gibi ürünlerle güvenli ve etkin koruma sağlıyoruz.
              </p>
              <div className="mt-3 flex gap-3">
                <Link to="/hizmetler/koruyucu-hekimlik" className="btn-secondary">Koruyucu Hekimlik</Link>
                <Link to="/hizmetler/agiz-ve-dis" className="btn-secondary">Ağız ve Diş</Link>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-2">Adres ve İletişim</h2>
              <p className="text-gray-700 leading-relaxed">
                Karaköprü / Yenişehir bölgesine yakın konumdayız. Adres: {contactInfo.address}
              </p>
              <ul className="mt-3 text-gray-700 space-y-1">
                <li><strong>Telefon:</strong> {contactInfo.phone}</li>
                <li><strong>WhatsApp:</strong> {contactInfo.whatsapp}</li>
                <li><strong>E‑posta:</strong> {contactInfo.email}</li>
              </ul>
            </div>
          </article>

          <aside className="space-y-4">
            <img src="/klinik.webp" alt="Şanlıurfa Veteriner Kliniği" className="rounded-xl border" loading="lazy" />
            <div className="rounded-xl border p-4 bg-primary-50">
              <h3 className="font-semibold text-primary-700 mb-2">Hızlı Randevu</h3>
              <p className="text-sm text-primary-900 mb-3">Acil durumlar ve fiyat bilgisi için hemen ulaşın.</p>
              <div className="flex gap-2">
                <a href={telHref} className="btn-primary flex-1 text-center">Ara</a>
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-secondary flex-1 text-center">WhatsApp</a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  )
}

export default SanliurfaVeterinerPage


